import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, NavigationEnd, RouterModule, Event, NavigationError } from '@angular/router';
import { AuthService } from '../core/services/auth.service';
import { AccountInfo } from '@azure/msal-browser';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { LastRefreshService } from './last-refresh.service';
import { LoaderComponent } from '../shared/components/loader/loader.component';

@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    standalone: true,
    imports: [RouterModule, CommonModule, LoaderComponent]
})
export class LayoutComponent implements OnInit, OnDestroy {
    isQueryInsightsCollapsed = false;
    userAccount: AccountInfo | null = null;

    // User menu state
    userMenuOpen: boolean = false;
    helpMenuOpen: boolean = false;

    readonly mailtoFeatureRequest =
        'mailto:insightshelp@aip.org?subject=Feature%20Request&body=Please%20complete%20the%20fields%20below:%0D%0A%0D%0AFeature%20Title:%20%0D%0AWhat%20problem%20does%20this%20feature%20solve?%20%0D%0ADescribe%20your%20idea%20in%201%E2%80%933%20sentences:%20%0D%0AWho%20is%20the%20primary%20audience?%20%0D%0APriority%20(High/Medium/Low):%20%0D%0AAdditional%20notes:%20';

    readonly mailtoIssueReport =
        'mailto:insightshelp@aip.org?subject=Issue%20Report&body=Please%20describe%20the%20issue%20below:%0D%0A%0D%0APage%20Name/URL:%20%0D%0AType%20of%20Issue%20(broken%20link,%20missing%20content,%20layout,%20accessibility,%20other):%20%0D%0ADescription%20of%20Problem:%20%0D%0ASteps%20to%20Reproduce%20(if%20applicable):%20%0D%0AAdditional%20details:%20';
    
    // Sidebar state
    sidebarCollapsed = false; // Already set to be expanded by default
    authorSubmenuOpen = true; // Change from false to true to have author submenu expanded by default
    campaignSubmenuOpen = true; // Add this property if needed
    institutionsMenuOpen = false;
    articleSubmenuOpen = true;
    toolsSubmenuOpen = true;

    // Data refresh timer properties
    lastRefreshTime: string = '';
    isRefreshTimeLoading: boolean = true;

    constructor(
        private authService: AuthService,
        private router: Router,
        private lastRefreshService: LastRefreshService
    ) {
        // Auto-open institutions menu if on institutions route
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            if (event.url.includes('/institutions')) {
                this.institutionsMenuOpen = true;
            }
        });
    }

    ngOnInit(): void {
        // Initialize with sidebar expanded
        this.adjustMainContent(); // Make sure classes are applied on init
        
        // Get user account
        this.userAccount = this.authService.getAccount();
        
        // Check routes to auto-open submenus
        this.checkRoutes();
        
        // Listen for route changes to update submenu state
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.checkRoutes();
        });

        // Add error handling to router events
        this.router.events.subscribe((event: Event) => {
            if (event instanceof NavigationError) {
                console.error('Navigation Error:', event.error);
            }
        });

        // Initialize data refresh timer
        this.getLastRefreshTime();
    }

    ngOnDestroy(): void {
        // No need to clear interval as it's removed
    }

    // Toggle sidebar collapsed state
    toggleSidebar(): void {
        this.sidebarCollapsed = !this.sidebarCollapsed;
        this.adjustMainContent();
    }
    
    // Toggle author submenu
    toggleAuthorSubmenu(): void {
        this.authorSubmenuOpen = !this.authorSubmenuOpen;
    }
    
    // Toggle campaign submenu
    toggleCampaignSubmenu(): void {
        this.campaignSubmenuOpen = !this.campaignSubmenuOpen;
    }

    toggleArticleSubmenu(): void {
        this.articleSubmenuOpen = !this.articleSubmenuOpen;
    }

    toggleToolsSubmenu(): void {
        this.toolsSubmenuOpen = !this.toolsSubmenuOpen;
    }
    
    // Toggle institutions menu
    toggleInstitutionsMenu(): void {
        this.institutionsMenuOpen = !this.institutionsMenuOpen;
    }
    
    // Check if author menu should be active
    isAuthorMenuActive(): boolean {
        return this.router.url.includes('/author-analytics');
    }

    isArticleMenuActive(): boolean {
        return  this.router.url.includes('/articles') || 
                this.router.url.includes('/article-details') ||
                this.router.url.includes('/article-usage');
    }

    isToolsMenuActive(): boolean {
        return this.router.url.includes('/sales-lead-lookup') || 
               this.router.url.includes('/author-contact') ||
               this.router.url.includes('/thesaurus-lookup') ||
               this.router.url.includes('/term-count') ||
               this.router.url.includes('/lead-evaluation');
    }    
    
    // Check if campaign menu should be active
    isCampaignMenuActive(): boolean {
        return this.router.url.includes('/campaign-analytics') || 
               this.router.url.includes('/campaign-performance') ||
               this.router.url.includes('/hum-campaigns') ||
               this.router.url.includes('/aet-funnel');
    }
    
    // Check if institutions menu should be active
    isInstitutionsActive(): boolean {
        return this.router.url.includes('/institutions');
    }
    
    // Check routes to auto-open submenus
    private checkRoutes(): void {
        if (this.router.url.includes('/author-analytics')) {
            this.authorSubmenuOpen = true;
        }
        
        if (this.router.url.includes('/campaign-analytics') || 
            this.router.url.includes('/campaign-performance') ||
            this.router.url.includes('/hum-campaigns') ||
            this.router.url.includes('/aet-funnel')) {
            this.campaignSubmenuOpen = true;
        }

        if (this.router.url.includes('/sales-lead-lookup') || 
            this.router.url.includes('/author-contact') ||
            this.router.url.includes('/thesaurus-lookup') ||
            this.router.url.includes('/term-count') ||
            this.router.url.includes('/lead-evaluation')) {
            this.toolsSubmenuOpen = true;
        }
    }

    logout(): void {
        this.authService.logout();
    }

    toggleUserMenu(event?: MouseEvent): void {
        event?.stopPropagation();
        this.userMenuOpen = !this.userMenuOpen;
        if (!this.userMenuOpen) {
            this.helpMenuOpen = false;
        }
    }

    toggleHelpMenu(event?: MouseEvent): void {
        event?.stopPropagation();
        this.helpMenuOpen = !this.helpMenuOpen;
    }

    closeUserMenu(): void {
        this.userMenuOpen = false;
        this.helpMenuOpen = false;
    }

    @HostListener('document:click')
    onDocumentClick(): void {
        if (this.userMenuOpen) {
            this.closeUserMenu();
        }
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent): void {
        if (this.userMenuOpen) {
            event.preventDefault();
            this.closeUserMenu();
        }
    }

    onQueryInsightsToggle(collapsed: boolean): void {
        this.isQueryInsightsCollapsed = collapsed;
    }

    // Get user initials for the avatar
    getUserInitials(): string {
        const rawName = this.userAccount?.name?.trim();
        if (!rawName) return 'U';

        // Remove any parenthetical tag(s) like "(Consultant)"
        const withoutParens = rawName.replace(/\s*\([^)]*\)\s*/g, ' ').trim();

        // Keep only letter sequences as tokens (handles "O'Connor", "Anne-Marie", etc. reasonably)
        const tokens = withoutParens.match(/[A-Za-z]+/g) || [];
        if (tokens.length === 0) return 'U';

        if (tokens.length === 1) {
            return tokens[0].substring(0, 2).toUpperCase();
        }

        const firstInitial = tokens[0]?.[0] ?? 'U';
        const lastInitial = tokens[tokens.length - 1]?.[0] ?? '';
        return (firstInitial + lastInitial).toUpperCase();
    }

    // Add this method to adjust main content
    adjustMainContent(): void {
        setTimeout(() => {
            const mainContent = document.querySelector('.main-content');
            if (mainContent) {
                if (this.sidebarCollapsed) {
                    mainContent.classList.remove('sidebar-expanded');
                } else {
                    mainContent.classList.add('sidebar-expanded');
                }
            }
        });
    }

    navigateToCampaignAnalytics(): void {
        console.log('Navigating to campaign analytics');
        this.router.navigate(['/campaign-analytics']).then(
            success => console.log('Navigation result:', success),
            error => console.error('Navigation error:', error)
        );
    }

    getLastRefreshTime(): void {
        this.isRefreshTimeLoading = true;
        this.lastRefreshService.getLastRefreshTime().subscribe({
            next: (res: any) => {
                this.lastRefreshTime = res.lastRefresh;
                this.isRefreshTimeLoading = false;
            },
            error: (err) => {
                this.lastRefreshTime = 'Unavailable';
                this.isRefreshTimeLoading = false;
            }
        });
    }
} 